import { listAndParseAppsQuery } from '../../../app_library/api/queries';
import canOpenApp from '../../../app_library/functions/canOpenApp';
import findAppByLabel from '../../../app_library/functions/findAppByLabel';
import findAppByPackageName from '../../../app_library/functions/findAppByPackageName';
import openApp from '../../../app_library/functions/openApp';
import { openPackageInGooglePlayStore } from '../../../app_library/functions/openAppInGooglePlay';
import { AppRecord } from '../../../app_library/types';
import { createOutputTextItem } from '../../../chat/ChatItem/functions';
import { AddChatItemFunction } from '../../../chat/types';
import { isUndefinedOrEmpty } from '../../../utils/strings';
import { OpenAppCommand } from '../types';

interface Params {
  command: OpenAppCommand;
  postToChat: AddChatItemFunction;
}

/** Command to open an app by label or package name */
const openAppCommand = async ({ command, postToChat }: Params) => {
  if (!validateOpenAppCommand(command)) return;

  const {
    data: { label, package_name, not_found, success_message, error },
  } = command;

  const postChatItem = (text: string) => postToChat(createOutputTextItem(text));
  const postError = () =>
    postChatItem(error ? error : "Sorry, I couldn't open that app");
  const postAppNotFound = () =>
    postChatItem(not_found ? not_found : "Sorry, I couldn't open that app");

  try {
    const { apps } = await listAndParseAppsQuery();

    const app = findAppInApprovedAppsList({
      package_name,
      apps,
      label,
    });

    if (!app) {
      postAppNotFound();
      return;
    }

    const can_open_app = await canOpenApp(app);

    if (can_open_app) {
      // App can be opened
      const res = await openApp(app);

      if (res) {
        success_message && postChatItem(success_message);
      } else {
        postError();
      }
    } else {
      const package_name_to_open = package_name || app.package_name;

      package_name_to_open
        ? openPackageInGooglePlayStore(package_name_to_open)
        : postAppNotFound();
    }
  } catch (ex) {
    postError();
  }
};

const validateOpenAppCommand = ({ data }: OpenAppCommand) => {
  if (!data) return false;

  const { label, package_name } = data;

  if (isUndefinedOrEmpty(label) && isUndefinedOrEmpty(package_name))
    return false;

  return true;
};

interface FindAppParams {
  apps: AppRecord[];
  package_name?: string;
  label?: string;
}

const findAppInApprovedAppsList = ({
  package_name,
  apps,
  label,
}: FindAppParams): AppRecord | undefined => {
  if (package_name) {
    return findAppByPackageName({ package_name, apps });
  } else if (label) {
    return findAppByLabel({ label, apps });
  }

  return;
};

export default openAppCommand;
